import { cx } from '@linaria/core';
import { useRef, useState, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { observeDomMutation } from '@pm2/common-utils';
import { floatingButtonStyles } from '@pm2/react-utils';
import { pm2SitemapStore, findPageDescriptorByPath } from '@pm2/sitemap-utils';
import { useLocation } from '@provider/navigation';
import { FloatingActionButton, ComplexNotebookIcon, GuideBubble } from '@ui/kit';
import { useSelectionRange, useLoaderState, CSSRefTransition } from '@utils/react';
import { useNotebook } from './hook.notebook.js';
import { PULSATION_DURATION_MS, iconCls, closeIconCls, FADE_DURATION_MS, buttonCls, badgeCls, notebookContainerCls, notebookCls } from './notebook-button.style.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { highlightNotesOnPage } from './utils/highlightNotesOnPage.js';
import { Aside } from '../../aside/Aside.js';
import { createNoteFromSelection } from './utils/createNoteFromSelection.js';
import { parseNotePlacementInfo } from './utils/parseNotePlacementInfo.js';

function NotebookButton({
  wrapperClassName,
  allowNoteCreation
}) {
  const buttonRef = useRef(null);
  const notebookRef = useRef(null);
  const location = useLocation();
  const sitemapState = pm2SitemapStore.useSelector(state => state);
  const page = findPageDescriptorByPath(sitemapState, location.pathname);
  const selection = useSelectionRange("article.gPortal-page", {
    enabled: !!allowNoteCreation
  });
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showTooltips, setShowTooltips] = useState(false);
  const tooltipTimeoutRef = useRef(undefined);
  const [isCreatingNote, setCreatingNote] = useState(false);
  const isLoading = useLoaderState(isCreatingNote, {
    animationIterationMs: PULSATION_DURATION_MS
  });
  const {
    notes,
    backlinkContext,
    isUnavailable,
    isDisabled,
    isOpen,
    isExpanded,
    resetBacklinkContext,
    open,
    close
  } = useNotebook(notebookRef);

  // deferred showing of tooltips when user has made a selection
  useEffect(() => {
    const hasSelection = selection !== undefined;
    window.clearTimeout(tooltipTimeoutRef.current);
    if (hasSelection && !showTooltips) {
      tooltipTimeoutRef.current = window.setTimeout(() => {
        setShowTooltips(true);
      }, 200);
    } else if (!hasSelection && showTooltips) {
      setShowTooltips(false);
    }
  }, [sitemapState, selection, showTooltips]);
  const toggleNotebook = () => {
    if (isDisabled) {
      return;
    }
    setHasUserInteracted(true);
    if (isOpen) {
      close();
      return;
    }
    if (selection === undefined) {
      // if the notebook hasn't been opened yet, and the user hasn't made any
      // selections then simply open the notebook "as is"
      open();
      return;
    }

    // ... otherwise create a new note from the user's current selection and
    // open that in the notebook
    setCreatingNote(true);
    createNoteFromSelection(sitemapState, location.pathname, selection)
    // if the user has made a selection, then we need to create a new note
    // with this text auto-highlighted
    .then(noteId => {
      setCreatingNote(false);
      open({
        noteId
      });

      // Clear the selection once the note has been made, the note
      // highlighting will take over instead.
      window.getSelection()?.empty();
    }).catch(err => {
      setCreatingNote(false);
      alert("Der opstod en fejl da vi forsøgte at oprette din note, vi beklager. Prøv igen.");
      throw err;
    });
  };

  // extract list of notes associated with the current page based on context
  // and placement info
  const notesOnCurrentPage = notes.filter(note => page && note.context?.page?.id === page.id || parseNotePlacementInfo(note.context?.placementInfo)?.path === location.pathname);

  // highlight notes on the current page
  useEffect(() => {
    setTimeout(() => {
      highlightNotesOnPage(notesOnCurrentPage, backlinkContext, resetBacklinkContext);
    }, 0);
    return observeDomMutation(() => highlightNotesOnPage(notesOnCurrentPage, backlinkContext, resetBacklinkContext));
  },
  // notesOnCurrentPage is directly derived from notes based on location, so
  // we can safely disable the exhaustive-deps rule here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [notes, backlinkContext, location.asPath, sitemapState.hasRenderedFirstPage]);
  const label = isOpen ? "Luk" : isDisabled ? "Denne funktion kan ikke tilgås i øjeblikket." : selection !== undefined ? "Notér" : "Åbn notesbog";
  if (isUnavailable) {
    return null;
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("div", {
      className: cx(wrapperClassName, floatingButtonStyles.buttonWrapperCls),
      children: [/*#__PURE__*/jsxs(FloatingActionButton, {
        ref: buttonRef,
        className: cx(buttonCls, floatingButtonStyles.buttonCls, isOpen && !isExpanded && "active"),
        disabled: isDisabled,
        onClick: toggleNotebook,
        tooltip: {
          placement: "left",
          body: label,
          ariaType: isDisabled ? "description" : "label"
        },
        children: [/*#__PURE__*/jsx(FloatingActionButton.Icon, {
          contentKey: "notebook",
          active: !isOpen,
          children: /*#__PURE__*/jsx(ComplexNotebookIcon, {
            className: cx(iconCls, isLoading.current && "pulsating"),
            pencilOnly: selection !== undefined
          })
        }), /*#__PURE__*/jsx(FloatingActionButton.Icon, {
          contentKey: "close",
          active: isOpen,
          children: /*#__PURE__*/jsx("span", {
            className: closeIconCls
          })
        }), /*#__PURE__*/jsx(FloatingActionButton.Icon, {
          children: /*#__PURE__*/jsx(TransitionGroup, {
            component: null,
            children: notesOnCurrentPage.length > 0 && /*#__PURE__*/jsx(CSSRefTransition, {
              classNames: buttonCls,
              timeout: FADE_DURATION_MS,
              children: ref => /*#__PURE__*/jsx("span", {
                ref: ref,
                className: badgeCls,
                children: notesOnCurrentPage.length
              })
            })
          })
        })]
      }), /*#__PURE__*/jsx(GuideBubble, {
        id: "notebook",
        className: floatingButtonStyles.guideBubbleCls,
        popperPlacement: "left",
        label: "Læs om hvordan du bruger notesbogen her",
        title: "Åbn notesbog",
        hasUserInteracted: hasUserInteracted,
        children: "Klik her og skriv en note. Du kan ogs\xE5 markere et tekststykke, klikke p\xE5 knappen og skrive en note. Du kan altid tilg\xE5 dine gamle noter, ved at \xE5bne notesbogen her."
      })]
    }), /*#__PURE__*/jsx(Aside, {
      persistent: true,
      align: "right",
      expanded: isOpen,
      onClose: close,
      children: /*#__PURE__*/jsx("div", {
        className: notebookContainerCls,
        children: /*#__PURE__*/jsx("div", {
          ref: notebookRef,
          className: cx(notebookCls, isExpanded && "isExpanded")
        })
      })
    })]
  });
}

export { NotebookButton };
//# sourceMappingURL=notebook-button.js.map
